/**
 * Turning a failed `getUserMedia` call into something staff can act on.
 *
 * The browser rejects with a DOMException whose `name` says what went wrong
 * but whose `message` is browser-specific and often useless (Chrome leaves it
 * empty for a missing device; Safari's reads like a spec). We key on `name`.
 */

export type MicErrorKind = "blocked" | "not_found" | "in_use";

export type BrowserKind =
  | "chrome"
  | "edge"
  | "firefox"
  | "safari"
  | "ios"
  | "other";

/** Returns the kind of microphone failure, or null for anything that isn't
 *  one (an API error, a missing user), which callers handle as before. */
export function classifyMicError(error: unknown): MicErrorKind | null {
  const name = (error as { name?: unknown } | null)?.name;
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "blocked";
    case "NotFoundError":
    case "OverconstrainedError":
      return "not_found";
    case "NotReadableError":
    case "AbortError":
      return "in_use";
    default:
      return null;
  }
}

export function detectBrowser(
  userAgent: string,
  maxTouchPoints = 0,
): BrowserKind {
  // iOS first: every iOS browser is WebKit and takes its mic setting from the
  // system Settings app, whatever the UA says about Chrome or Firefox. iPad
  // Safari asks for desktop sites by default and so calls itself a Mac; a Mac
  // with a touch screen is the tell, as in useSetupAudioDevices.
  if (/iPad|iPhone|iPod/.test(userAgent)) return "ios";
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return "ios";
  if (/Edg\//.test(userAgent)) return "edge";
  if (/Firefox\//.test(userAgent)) return "firefox";
  if (/Chrome\/|CriOS\//.test(userAgent)) return "chrome";
  if (/Safari\//.test(userAgent)) return "safari";
  return "other";
}

export const BROWSER_LABELS: Record<BrowserKind, string> = {
  chrome: "Chrome",
  edge: "Edge",
  firefox: "Firefox",
  safari: "Safari",
  ios: "iPhone or iPad",
  other: "your browser",
};

/** Steps to unblock the microphone for this site, per browser. */
export const UNBLOCK_STEPS: Record<BrowserKind, string[]> = {
  chrome: [
    "Click the icon to the left of the web address (it looks like two sliders or a padlock).",
    "Find Microphone and switch it to Allow.",
    "Reload the page, then press record again.",
  ],
  edge: [
    "Click the padlock to the left of the web address.",
    "Choose Permissions for this site, then set Microphone to Allow.",
    "Reload the page, then press record again.",
  ],
  firefox: [
    "Click the microphone or padlock icon to the left of the web address.",
    "Remove the Blocked setting next to Use the Microphone.",
    "Reload the page, press record again, and choose Allow when asked.",
  ],
  safari: [
    "In the menu bar, click Safari, then Settings for this website.",
    "Set Microphone to Allow.",
    "Reload the page, then press record again.",
  ],
  ios: [
    "Open the Settings app and scroll down to the browser you are using (for example Safari or Chrome).",
    "Tap Microphone and choose Allow.",
    "Come back to this page, reload it, then press record again.",
  ],
  other: [
    "Open the site settings for this page (usually the padlock next to the web address).",
    "Set Microphone to Allow.",
    "Reload the page, then press record again.",
  ],
};

/** The computer can block the browser itself, which no browser setting fixes. */
export const SYSTEM_STEPS: Partial<Record<BrowserKind, string>> = {
  chrome:
    "Still blocked? On a Mac, open System Settings, then Privacy & Security, then Microphone, and turn on your browser. On Windows, open Settings, then Privacy, then Microphone.",
  edge: "Still blocked? On Windows, open Settings, then Privacy, then Microphone, and turn on Microsoft Edge.",
  firefox:
    "Still blocked? On a Mac, open System Settings, then Privacy & Security, then Microphone, and turn on Firefox.",
  safari:
    "Still blocked? Open System Settings, then Privacy & Security, then Microphone, and turn on Safari.",
};
